import React, { useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Download } from '@mui/icons-material';

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

const formatDocId = (docType, docId) => {
  if (!docId) return '';
  if (docType === 'CPF') {
    return docId.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (docType === 'CNPJ') {
    return docId.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return docId;
};

const formatOwners = (owners) => {
  if (!owners || !Array.isArray(owners)) return '';
  return owners
    .map((o) => `${o.fullName} (${formatDocId(o.docType, o.docId)})`)
    .join(' - ');
};

const formatBaseYear = (baseYear) => {
  if (!baseYear) return '';
  const start = formatDate(baseYear.startDate);
  const end = formatDate(baseYear.endDate);
  return start && end ? `${start} - ${end}` : '';
};

const getCompensationsTotal = (compensations) => {
  if (!compensations || !Array.isArray(compensations)) return 0;
  return compensations.reduce((sum, c) => sum + (Number(c.offset) || 0), 0);
};

const buildRows = (certifications) => {
  const sorted = [...certifications].sort((a, b) => {
    const dateA = a.data.registrationDate?.toDate?.() ?? new Date(0);
    const dateB = b.data.registrationDate?.toDate?.() ?? new Date(0);
    return dateA - dateB;
  });

  return sorted.map((cert, index) => ({
    num: index + 1,
    certificationId: cert.id,
    certificationDate: formatDate(cert.data.date),
    registrationDate: formatDate(cert.data.registrationDate),
    car: cert.data.car ?? '',
    owners: formatOwners(cert.data.owners),
    requesterName: cert.data.requester?.fullName ?? '',
    requesterPhone: cert.data.requester?.phone ?? '',
    requesterEmail: cert.data.requester?.email ?? '',
    matriculation: cert.data.matriculation?.join(' - ') ?? '',
    propertyName: cert.data.address?.propertyName ?? '',
    propertyArea: cert.data.propertyArea ?? '',
    country: cert.data.address?.country ?? '',
    state: cert.data.address?.state ?? '',
    city: cert.data.address?.city ?? '',
    longitude: cert.data.address?.longitude ?? '',
    latitude: cert.data.address?.latitude ?? '',
    baseYear: formatBaseYear(cert.data.baseYear),
    carbonCreditTotal: cert.data.carbonCredit ?? '',
    carbonCreditCompensated: getCompensationsTotal(cert.data.compensations),
    status: cert.data.status?.custody ?? '',
  }));
};

const COLUMNS = [
  { key: 'num', header: 'Num' },
  { key: 'certificationId', header: 'Certification ID' },
  { key: 'certificationDate', header: 'Certification Date' },
  { key: 'registrationDate', header: 'Registration Date' },
  { key: 'car', header: 'CAR' },
  { key: 'owners', header: 'Owners' },
  { key: 'requesterName', header: 'Requester Name' },
  { key: 'requesterPhone', header: 'Requester Phone' },
  { key: 'requesterEmail', header: 'Requester Email' },
  { key: 'matriculation', header: 'Matriculation' },
  { key: 'propertyName', header: 'Property Name' },
  { key: 'propertyArea', header: 'Property Area (hectare)' },
  { key: 'country', header: 'Country' },
  { key: 'state', header: 'State' },
  { key: 'city', header: 'City' },
  { key: 'longitude', header: 'Longitude' },
  { key: 'latitude', header: 'Latitude' },
  { key: 'baseYear', header: 'Base Year' },
  { key: 'carbonCreditTotal', header: 'Carbon Credits (Total)' },
  { key: 'carbonCreditCompensated', header: 'Carbon Credits (Compensated)' },
  { key: 'status', header: 'Status' },
];

const escapeCSV = (value) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCSV = (rows) => {
  const header = COLUMNS.map((c) => c.header).join(',');
  const dataRows = rows.map((row) =>
    COLUMNS.map((c) => escapeCSV(row[c.key])).join(',')
  );
  return [header, ...dataRows].join('\n');
};

const currentDateStr = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReportDialog = ({ open, onClose, certifications }) => {
  const rows = useMemo(() => buildRows(certifications), [certifications]);

  const handleDownload = () => {
    const csv = buildCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentDateStr()}_ABCarbonCertificationsReport.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Certifications Report ({rows.length})</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <TableContainer component={Paper} sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.key} sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                    {col.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.certificationId} hover>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key} sx={{ whiteSpace: 'nowrap' }}>
                      {row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          startIcon={<Download />}
        >
          Download CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportDialog;
